import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { Logo } from '../assets/Logo';
import { PasswordField } from '../components/PasswordField';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      
      // Store the auth token
      localStorage.setItem('authToken', token);
      
      // Redirect to home
      navigate('/home');
    } catch (err: any) {
      // Translate Firebase error codes to Spanish
      let errorMessage = 'Error al iniciar sesión';
      
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        errorMessage = 'Correo electrónico o contraseña incorrectos';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Correo electrónico inválido';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'Esta cuenta ha sido deshabilitada';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Demasiados intentos fallidos. Por favor intente más tarde';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Error de conexión. Verifique su internet';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-6 h-full w-full">
      {/* Login Card */}
      <div className="bg-white shadow-lg rounded-xl w-full max-w-[420px] p-8 md:p-10 flex flex-col gap-6 relative z-10">
        {/* Header Section */}
        <div className="flex flex-col items-center justify-center gap-2 mb-2">
          {/* Logo Icon */}
          <Logo />
          <h1 className="text-[#101822] text-3xl font-bold mt-2">Bienvenido</h1>
          <p className="text-slate-500 text-sm font-medium">Ingresa tus credenciales para acceder</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full">
          {/* Email Field */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[#101822] text-sm font-semibold leading-normal ml-1">
              Email
            </label>
            <div className="flex w-full items-center rounded-xl border border-[#dbe0e6] bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30 transition-all h-12 overflow-hidden">
              <div className="pl-4 flex items-center justify-center text-slate-400">
                <span className="material-symbols-outlined text-[20px]">mail</span>
              </div>
              <input
                className="flex-1 w-full bg-transparent border-none text-[#101822] placeholder:text-slate-400 focus:ring-0 text-base h-full py-0 pl-3 pr-4"
                placeholder="correo@ejemplo.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Password Field */}
          <PasswordField
            id="password"
            label="Contraseña"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            required
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
          />

          {/* Primary Action Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-blue-600 text-white font-bold h-12 rounded-xl text-base tracking-wide shadow-sm hover:shadow-md transition-all mt-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}</span>
            {!loading && <span className="material-symbols-outlined text-[20px]">arrow_forward</span>}
          </button>
        </form>

        {/* Signup Link */}
        <div className="text-center pt-4 border-t border-slate-200">
          <p className="text-slate-500 text-sm">
            ¿No tienes cuenta?{' '}
            <a 
              href="/signup" 
              className="text-primary font-semibold hover:underline"
            >
              Regístrate aquí
            </a>
          </p>
        </div>
      </div>

      {/* Decorative Background Element (Subtle) */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0 overflow-hidden">
        {/* Abstract medical blue gradient */}
        <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-blue-100/50 blur-[120px]"></div>
        <div className="absolute -bottom-[10%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-50/50 blur-[100px]"></div>
      </div>
    </div>
  );
};
